<!-- eslint-disable padding-line-between-statements -->
<template>
  <CCard class="shadow-sm border-0 mb-4">
    <CCardHeader class="bg-white border-bottom-0 pt-3 px-3 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
      <div class="d-flex align-items-center">
        <h5 class="font-weight-bold mb-0 mr-3">
          RPC Performance by Category {{ dateRangeText }}
        </h5>
      </div>
      <div class="d-flex align-items-center mt-2 mt-sm-0 w-100 w-sm-auto">
        <select
          v-model="internalAccountId"
          class="custom-select custom-select-sm"
          style="max-width: 250px;"
        >
          <option :value="null">
            All Accounts
          </option>
          <option
            v-for="acc in buyerAccounts"
            :key="acc.id"
            :value="acc.id"
          >
            {{ acc.name }}
          </option>
        </select>
      </div>
    </CCardHeader>
    <CCardBody class="px-3 pb-3">
      <!-- Chart -->
      <div
        v-if="chartData.datasets.length > 0"
        style="width: 100%; position: relative;"
      >
        <CChartLine
          :datasets="chartData.datasets"
          :labels="chartData.labels"
          :options="chartOptions"
          style="height: 290px; width: 100%"
        />
      </div>
      <div
        v-else-if="isLoading"
        class="text-center py-5"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        >
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div
        v-else
        class="text-center py-5 text-muted"
      >
        No data available for the selected date range
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { CChartLine } from '@coreui/vue-chartjs'

export default {
  name: 'RpcCategoryChart',
  components: {
    CChartLine
  },
  props: {
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    accountId: {
      type: String,
      default: null
    },
    categoryName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      defaultDaysBack: 7
    }
  },
  computed: {
    ...mapGetters('insights', {
      apiDataPoints: 'campaignChartData',
      isLoading: 'isCampaignChartLoading'
    }),
    dateRangeText() {
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate).toLocaleDateString()
        const end = new Date(this.endDate).toLocaleDateString()
        return `(${start} - ${end})`
      }

      return `(Last ${this.defaultDaysBack} Days)`
    },
    ...mapState('accounts', ['buyerAccounts']),

    internalAccountId: {
      get() {
        return this.accountId
      },
      set(val) {
        this.$emit('update:account-id', val)
      }
    },

    chartData() {
      const records = this.apiDataPoints || []

      if (records.length === 0) {
        return { labels: [], datasets: [] }
      }

      // 1. Get unique dates and sort them
      // Fallback to datesByYearMonthDay if date is missing (backend DTO issue)
      const dates = [...new Set(records.map(r => r.date || r.datesByYearMonthDay))].sort()
      const formattedDates = dates.map(d => {
        if (!d) {
          return 'Invalid Date'
        }

        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      })

      // 2. Group records by Category Name
      const categories = {}
      records.forEach(r => {
        const cat = r.categoryName || 'Unknown'
        const dateKey = r.date || r.datesByYearMonthDay
        
        if (!categories[cat]) {
          categories[cat] = {}
        }

        // Map date to record for easy lookup
        if (dateKey) {
          categories[cat][dateKey] = r
        }
      })

      // 3. Create datasets
      const datasets = []
      const colorPalette = [
        '#321fdb', '#e55353', '#f9b115', '#2eb85c', '#3399ff', 
        '#6f42c1', '#20c997', '#ffc107', '#17a2b8', '#6610f2'
      ] // Reuse palette or extend as needed

      Object.keys(categories).forEach((cat, index) => {
        const color = colorPalette[index % colorPalette.length]
        const catData = categories[cat]

        const netData = dates.map(d => {
          const record = catData[d]
          // Use Billable Gross RPC 
          return record ? (record.earningsPerBillableCallGross || 0) : 0
        })

        // Add "Billable" dataset (Solid)
        datasets.push({
          label: cat,
          backgroundColor: 'transparent',
          borderColor: color,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: color,
          data: netData,
          borderWidth: 2
        })
      })

      return {
        labels: formattedDates,
        datasets
      }
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        height: 250,
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            gridLines: {
              drawOnChartArea: false,
              color: '#ced4da'
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: (value) => '$' + value
            },
            gridLines: {
              borderDash: [2, 4],
              color: '#ced4da'
            },
            scaleLabel: {
              display: true,
              labelString: 'RPC ($)'
            }
          }]
        },
        elements: {
          point: {
            radius: 3,
            hitRadius: 10,
            hoverRadius: 4
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || ''
              const value = tooltipItem.yLabel
              return `${label}: $${Number(value).toFixed(2)}`
            }
          }
        }
      }
    }
  },
  watch: {
    startDate() {
      this.fetchData()
    },
    endDate() {
      this.fetchData()
    },
    accountId() {
      this.fetchData()
    },
    categoryName() {
      this.fetchData()
    }
  },
  async mounted() {
    await this.fetchData()
    if (!this.buyerAccounts || !this.buyerAccounts.length) {
      this.getApprovedBuyerAccounts()
    }
  },
  methods: {
    ...mapActions('insights', ['fetchCategoryRPC']),
    ...mapActions('accounts', ['getApprovedBuyerAccounts']),
    async fetchData() {
      let start = this.startDate
      let end = this.endDate

      // Default to last 7 days if no range provided
      if (!start || !end) {
        const endDateObj = new Date()
        const startDateObj = new Date()
        startDateObj.setDate(endDateObj.getDate() - this.defaultDaysBack)
        
        start = startDateObj.toISOString()
        end = endDateObj.toISOString()
      }

      await this.fetchCategoryRPC({
        startDate: start,
        endDate: end,
        accountId: this.accountId,
        categoryName: this.categoryName
      })
    }
  }
}
</script>
